import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Input, Select, DatePicker, Modal, Form, 
  notification, Tag, Badge, Drawer, Row, Col, Typography, 
  Popconfirm, Tooltip, Card, Statistic, Divider, Upload,
  Progress, Alert, Result, List
} from 'antd';
import {
  PlusOutlined, SearchOutlined, FilterOutlined, EditOutlined,
  DeleteOutlined, EyeOutlined, ReloadOutlined, SettingOutlined,
  UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined,
  CalendarOutlined, TeamOutlined, SafetyOutlined, UploadOutlined, 
  DownloadOutlined, FileTextOutlined, CheckCircleOutlined, 
  ExclamationCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import lib, { myApi } from '../../assets/utils/lib';
import { uri } from '../../assets/utils/http-request';
import moment from 'moment';
import { useBulkUploadProgress } from './hooks/useBulkUploadProgress';
import BulkUploadProgress from './components/BulkUploadProgressIndicator';
import {
  formatPlanEnrollmentErrorMessage,
  formatPlanEnrollmentRowError,
} from '../plans/planCatalogHelpers';
import EnrollmentPausedBanner from './components/EnrollmentPausedBanner';
import SubBandPurchasePicker from '../../components/PlanManagement/SubBandPurchasePicker';
import useFormConfig from '../../hooks/useFormConfig';
import { states as stateOptions } from '../../assets/utils/config';
import {
  enrollmentPausedNotification,
  isEnrollmentNeedsResolution,
  resolvePlainOrgCode,
} from './enrollmentConflictHelpers';
import './orgs-dashboard.css';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const BulkUploadManagement = ({ socialNo, organizationData, tabCounts = {} }) => {
  // Email job tracking
  const [emailJobId, setEmailJobId] = useState(null);
  
  // HTTP Polling progress tracking for email jobs
  const { progress, isProcessing, resetProgress, isConnected } = useBulkUploadProgress(emailJobId, !!emailJobId);

  const [onboardingRecords, setOnboardingRecords] = useState([]);
  const [orgPlanOptions, setOrgPlanOptions] = useState([]);
  // Band selection is scoped to what THIS organization has set up for the
  // chosen plan (Org Plan Management), not every band on the plan.
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [orgPlanError, setOrgPlanError] = useState(null);
  const [loadingOrgPlans, setLoadingOrgPlans] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} pending onboarding records`
  });
  
  // Filter and search state
  const [filters, setFilters] = useState({
    search: '',
    premium_type: '',
    state: '',
    city: '',
    status: '',
    has_email: '',
    has_social_no: '',
    is_corporate: '',
    additional_heads_min: '',
    additional_heads_max: '',
    created_date_from: '',
    created_date_to: '',
    sort_by: 'created_date',
    sort_order: 'DESC'
  });
  
  // UI state
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // CSV Upload state
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploadRowErrors, setUploadRowErrors] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [cleanExisting, setCleanExisting] = useState(true);
  const [showCleanConfirm, setShowCleanConfirm] = useState(false);
  const [uploadJobId, setUploadJobId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [enrollmentPause, setEnrollmentPause] = useState(null);
  const [exportingConflicts, setExportingConflicts] = useState(false);
  const pollIntervalRef = useRef(null);
  
  // Form instances
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadOnboardingRecords = useCallback(async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page,
        limit,
        ...(filters.created_date_from && { created_date_from: moment(filters.created_date_from).format('YYYY-MM-DD') }),
        ...(filters.created_date_to && { created_date_to: moment(filters.created_date_to).format('YYYY-MM-DD') })
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await myApi('GET', `/v2/corporations/${socialNo}/deals/`, params);

      if (response.data && !response.data.error) {
        setOnboardingRecords(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.pagination?.total || 0,
          pageSize: limit
        }));
      } else {
        notification.error({
          message: 'Error Loading Policies',
          description: response.data?.message || 'Failed to load policies data'
        });
      }
    } catch (error) {
      console.error('Error loading policies:', error);
      notification.error({
        message: 'Error Loading Policies',
        description: error.message || 'An unexpected error occurred'
      });
    } finally {
      setLoading(false);
    }
  }, [filters, socialNo]);

  const handleRefresh = () => {
    loadOnboardingRecords(pagination.current, pagination.pageSize);
  };

  // Initial load
  useEffect(() => {
    if (socialNo) {
      loadOnboardingRecords();
    }
  }, [loadOnboardingRecords, socialNo]);

  // Plain org code for UI/filters — route `socialNo` may be base64 path hash.
  const orgSocialNo = resolvePlainOrgCode(
    organizationData?.org_social_no,
    organizationData?.org_code,
    socialNo,
  );

  // Sub-band purchase (premium type -> family size -> dependents -> server
  // total). The picker is silent when the plan has no sub-band prices, and
  // the legacy Premium Type select shows instead.
  const [subBandsAvailable, setSubBandsAvailable] = useState(false);
  // Reference lists + form settings (admin-configured; falls back to built-ins).
  const cfg = useFormConfig({ forms: ['org_client'], lists: ['state'] });
  const applyCompositionToForm = (form, c) => {
    form.setFieldsValue({
      pm_band_id: c.base_band_id || undefined,
      pm_dependents: c.dependents && c.dependents.length ? JSON.stringify(c.dependents) : '',
      premium_type: c.premium_type,
      additional_heads: c.additional_heads,
      enrolee_size: c.enrolee_size ?? undefined,
      policy_cost: c.policy_cost ?? undefined,
    });
  };
  useEffect(() => {
    if (createModalVisible && orgSocialNo) {
      // In org context the record is always corporate — social_no and category are implied.
      createForm.setFieldsValue({ social_no: orgSocialNo, category: 'corporate' });
    }
  }, [createModalVisible, orgSocialNo, createForm]);

  // Plans assigned to this org via Manage plans — used for package dropdown + CSV guidance.
  useEffect(() => {
    if (!orgSocialNo) {
      setOrgPlanOptions([]);
      return undefined;
    }
    let mounted = true;
    setLoadingOrgPlans(true);
    setOrgPlanError(null);
    lib.getOrganizationPlanCatalog(orgSocialNo)
      .then((resp) => lib.parseOrganizationPlanCatalog(resp))
      .then((catalog) => {
        if (!mounted) return;
        if (!catalog.ok) {
          // A failed lookup used to render as "no plans assigned yet", which
          // sends the admin back to Manage plans to re-assign a plan that is
          // already there. Say that the lookup failed instead.
          setOrgPlanError(catalog.message || 'Could not load this organization\'s assigned plans.');
          setOrgPlanOptions([]);
          return;
        }
        setOrgPlanOptions(
          catalog.rows
            .filter((plan) => Number.isFinite(Number(plan?.plan_id)))
            .map((plan) => ({
              label: plan.plan_name || `Plan ${plan.plan_id}`,
              value: plan.plan_name || String(plan.plan_id),
              plan_id: Number(plan.plan_id),
              plan_name: plan.plan_name || '',
            })),
        );
      })
      .catch((e) => {
        if (!mounted) return;
        setOrgPlanError(
          e?.response?.data?.message || e?.message || 'Could not load this organization\'s assigned plans.',
        );
        setOrgPlanOptions([]);
      })
      .finally(() => {
        if (mounted) setLoadingOrgPlans(false);
      });
    return () => { mounted = false; };
  }, [orgSocialNo]);

  /**
   * Band picker scoped to this organization (Org Plan Management), falling
   * back to the legacy Individual/Family choice when the org has no bands set
   * up for the selected plan — so nothing regresses for un-migrated orgs.
   */
  const renderPremiumOrBandField = (form) => (
    <>
      {selectedPlanId ? (
        <Form.Item label="Premium" style={{ marginBottom: 8 }}>
          <SubBandPurchasePicker
            planId={selectedPlanId}
            socialNo={orgSocialNo}
            orgId={organizationData?.org_id}
            onAvailability={setSubBandsAvailable}
            onChange={(c) => applyCompositionToForm(form, c)}
            compact
          />
        </Form.Item>
      ) : null}
      {!selectedPlanId || !subBandsAvailable ? (
        <Form.Item name="premium_type" label="Premium Type" extra={!selectedPlanId ? 'Pick the insurance package first to see its sub-bands.' : undefined}>
          <Select placeholder="Select premium type">
            <Option value="individual">Individual</Option>
            <Option value="family">Family</Option>
          </Select>
        </Form.Item>
      ) : (
        <Form.Item name="premium_type" hidden><Input /></Form.Item>
      )}
      <Form.Item name="pm_band_id" hidden><Input /></Form.Item>
      <Form.Item name="pm_dependents" hidden><Input /></Form.Item>
      <Form.Item name="enrolee_size" hidden><Input /></Form.Item>
      <Form.Item name="policy_cost" hidden><Input /></Form.Item>
    </>
  );

  const renderInsurancePackageField = () => (
    <Form.Item
      name="insurance_package"
      label="Insurance Package"
      rules={[{ required: true, message: 'Select a plan assigned to this organization' }]}
      extra={
        // Three different states, three different next actions — telling them
        // apart is the difference between "assign a plan" and "this is a bug".
        // eslint-disable-next-line no-nested-ternary
        orgPlanError
          ? `${orgPlanError} The plans assigned under Manage plans could not be read${orgSocialNo ? ` for "${orgSocialNo}"` : ''}.`
          : (orgPlanOptions.length === 0 && !loadingOrgPlans
            ? 'No plans assigned yet. Use Manage plans on this organization first.'
            : 'Only plans assigned via Manage plans appear here.')
      }
      validateStatus={orgPlanError ? 'warning' : undefined}
    >
      <Select
        placeholder={loadingOrgPlans ? 'Loading org plans…' : 'Select plan'}
        loading={loadingOrgPlans}
        disabled={!orgPlanOptions.length}
        onChange={(value) => {
          const picked = orgPlanOptions.find((o) => o.value === value);
          setSelectedPlanId(picked?.plan_id ?? null);
        }}
        options={orgPlanOptions}
        showSearch
        optionFilterProp="label"
        notFoundContent={
          // eslint-disable-next-line no-nested-ternary
          loadingOrgPlans ? 'Loading…' : (orgPlanError ? 'Could not load plans' : 'No assigned plans')
        }
      />
    </Form.Item>
  );

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      premium_type: '',
      state: '',
      city: '',
      status: '',
      has_email: '',
      has_social_no: '',
      is_corporate: '',
      additional_heads_min: '',
      additional_heads_max: '',
      created_date_from: '',
      created_date_to: '',
      sort_by: 'created_date',
      sort_order: 'DESC'
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Create pending onboarding record
  const handleCreateBulkUpload = async (values) => {
    try {
      const response = await myApi('POST', `/v2/corporations/${socialNo}/deals/`, values);
      const body = response?.data ?? response;
      const data = body?.data || {};

      if (isEnrollmentNeedsResolution(body) || isEnrollmentNeedsResolution(data)) {
        setEnrollmentPause(data);
        setCreateModalVisible(false);
        createForm.resetFields();
        const note = enrollmentPausedNotification(data);
        notification.info({
          message: note.message,
          description: note.description,
          duration: 8,
        });
        return;
      }

      if (body && !body.error) {
        const updated = data?.action === 'policy_updated';
        notification.success({
          message: 'Success',
          description: body.message
            || (updated ? 'Existing policy updated successfully' : 'Onboarding record created successfully'),
        });
        setCreateModalVisible(false);
        createForm.resetFields();
        loadOnboardingRecords(pagination.current, pagination.pageSize);
      } else {
        notification.error({
          message: 'Error Creating Onboarding Record',
          description: formatPlanEnrollmentErrorMessage(body || response, {
            fallback: 'Failed to create onboarding record',
          }),
        });
      }
    } catch (error) {
      console.error('Error creating onboarding record:', error);
      notification.error({
        message: 'Error Creating Onboarding Record',
        description: formatPlanEnrollmentErrorMessage(error, {
          fallback: error.message || 'An unexpected error occurred',
        }),
      });
    }
  };

  // Update policy
  const handleUpdateOnboarding = async (values) => {
    try {
      const response = await myApi('PUT', `/v2/corporations/${socialNo}/deals/${selectedOnboarding.id}`, values);
      
      if (response.data && !response.data.error) {
        notification.success({
          message: 'Success',
          description: 'Onboarding record updated successfully'
        });
        setEditModalVisible(false);
        setSelectedOnboarding(null);
        editForm.resetFields();
        loadOnboardingRecords(pagination.current, pagination.pageSize);
      } else {
        notification.error({
          message: 'Error Updating Onboarding Record',
          description: formatPlanEnrollmentErrorMessage(response.data || response, {
            fallback: 'Failed to update onboarding record',
          }),
        });
      }
    } catch (error) {
      console.error('Error updating onboarding record:', error);
      notification.error({
        message: 'Error Updating Onboarding Record',
        description: formatPlanEnrollmentErrorMessage(error, {
          fallback: error.message || 'An unexpected error occurred',
        }),
      });
    }
  };

  // Delete policy
  const handleDeleteOnboarding = async (onboardingId) => {
    try {
      const response = await myApi('DELETE', `/v2/corporations/${socialNo}/deals/${onboardingId}?soft=true`);
      
      if (response.data && !response.data.error) {
        notification.success({
          message: 'Success',
          description: 'Onboarding record deleted successfully'
        });
        loadOnboardingRecords(pagination.current, pagination.pageSize);
      } else {
        notification.error({
          message: 'Error Deleting Onboarding Record',
          description: response.data?.message || 'Failed to delete onboarding record'
        });
      }
    } catch (error) {
      console.error('Error deleting onboarding record:', error);
      notification.error({
        message: 'Error Deleting Onboarding Record',
        description: error.message || 'An unexpected error occurred'
      });
    }
  };

  // Bulk actions
  const handleBulkAction = async (operation, value = null) => {
    if (selectedRowKeys.length === 0) {
      notification.warning({
        message: 'No Selection',
        description: 'Please select policies to perform bulk actions'
      });
      return;
    }

    setBulkActionLoading(true);
    try {
      const payload = {
        operation,
        lead_ids: selectedRowKeys,
        ...(value && { [operation.split('_')[1]]: value })
      };

      const response = await myApi('POST', `/v2/corporations/${socialNo}/deals/bulk`, payload);
      
      if (response.data && !response.data.error) {
        // Check if this is an email sending operation that returns a job_id
        if (operation === 'send_welcome_email' && response.data?.data?.job_id) {
          const jobId = response.data.data.job_id;
          setEmailJobId(jobId);
          notification.info({
            message: 'Email Job Started',
            description: `Sending welcome emails to ${selectedRowKeys.length} policies...`
          });
        } else {
          notification.success({
            message: 'Bulk Action Completed',
            description: `${operation} completed for ${selectedRowKeys.length} policies`
          });
        }
        
        setSelectedRowKeys([]);
        loadOnboardingRecords(pagination.current, pagination.pageSize);
      } else {
        notification.error({
          message: 'Bulk Action Failed',
          description: response.data?.message || 'Failed to perform bulk action'
        });
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      notification.error({
        message: 'Bulk Action Failed',
        description: error.message || 'An unexpected error occurred'
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // CSV Upload functionality (async with job polling)
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => () => {
    stopPolling();
  }, []);

  const startPollingStatus = (jobId) => {
    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await myApi('GET', `/v2/corporations/csv-jobs/${jobId}/status`);
        const data = statusResponse?.data;
        if (data && Number(data.error) === 0) {
          setUploadStatus(data.data);
          setUploadProgress(Number(data.data?.progress_percentage) || 0);

          // 'needs_resolution' is terminal too: the enrollment conflict scan
          // now runs inside the job, so the conflicts pause that used to come
          // back on the upload response arrives on a poll instead.
          if (['completed', 'failed', 'needs_resolution'].includes(data.data?.status)) {
            stopPolling();
            setUploading(false);
            const enrollment = data.data?.enrollment || null;

            if (data.data?.status === 'needs_resolution') {
              setEnrollmentPause(enrollment);
              const note = enrollmentPausedNotification(enrollment || {});
              notification.info({
                message: note.message,
                description: note.description,
                duration: 8,
              });
              setTimeout(() => loadOnboardingRecords(pagination.current, pagination.pageSize), 1000);
            } else if (data.data?.status === 'completed') {
              const summary = enrollment?.summary;
              setUploadResults({
                total_rows: summary?.total_rows ?? data.data?.total_rows,
                valid_rows: data.data?.successful_rows,
                inserted: summary?.onboarded ?? data.data?.successful_rows,
                details: data.data?.results || []
              });
              // Prefer the enrolment breakdown when the conflict scan ran — the
              // raw row count says nothing about what actually changed.
              const parts = [];
              if (summary) {
                if (summary.onboarded > 0) parts.push(`${summary.onboarded} onboarded`);
                if (summary.updated > 0) parts.push(`${summary.updated} updated`);
                if (summary.dismissed_as_same > 0) parts.push(`${summary.dismissed_as_same} dismissed as same`);
              }
              notification.success({
                message: 'CSV Upload Completed',
                description: parts.length
                  ? parts.join(' · ')
                  : `Processed ${data.data?.total_rows} rows`
              });
              setTimeout(() => loadOnboardingRecords(pagination.current, pagination.pageSize), 1000);
            } else {
              notification.error({
                message: 'CSV Upload Failed',
                description: data.data?.error_message || 'An error occurred during processing'
              });
            }
          }
        } else {
          throw new Error(data?.message || 'Failed to fetch job status');
        }
      } catch (err) {
        stopPolling();
        setUploading(false);
        notification.error({
          message: 'CSV Upload Status Error',
          description: err.message || 'Unable to track upload status'
        });
      }
    }, 2000);
  };

  // Removed WebSocket code - now using HTTP polling only for CSV upload

  // Pull per-row validation errors out of the many response shapes myApi may return.
  const extractUploadRowErrors = (resp) => {
    const candidates = [
      resp?.data?.rows,
      resp?.data?.data?.rows,
      resp?.rows,
      resp?.response?.data?.data?.rows,
    ];
    for (const list of candidates) {
      if (Array.isArray(list) && list.length) return list;
    }
    return [];
  };

  const handleCSVUpload = async (file, shouldCleanExisting = false) => {
    setUploading(true);
    setUploadProgress(0);
    setUploadResults(null);
    setUploadRowErrors(null);
    setUploadStatus(null);
    setUploadJobId(null);
    setEnrollmentPause(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Do not override headers here; myApi will include Authorization and set proper multipart headers
      const response = await myApi('POST', `/v2/corporations/${socialNo}/deals/upload?async=true&clean_existing=${shouldCleanExisting}`, formData);
      const body = response?.data ?? response;
      const data = body?.data || {};

      if (isEnrollmentNeedsResolution(body) || isEnrollmentNeedsResolution(data)) {
        setEnrollmentPause(data);
        setUploading(false);
        const note = enrollmentPausedNotification(data);
        notification.info({
          message: note.message,
          description: note.description,
          duration: 8,
        });
        loadOnboardingRecords(pagination.current, pagination.pageSize);
        return;
      }

      if (body && Number(body.error) === 0) {
        const jobId = data?.job_id;
        if (jobId) {
          setUploadJobId(jobId);
          setUploadStatus({ status: data?.status, total_rows: data?.total_rows, progress_percentage: 0 });
          startPollingStatus(jobId);
          notification.info({ message: 'CSV Upload Queued', description: 'Your upload has been queued for processing.' });
        } else {
          setUploading(false);
          const added = Number(data?.added_count) || 0;
          const updated = Number(data?.updated_count) || 0;
          const dismissed = Number(data?.dismissed_as_same_count) || 0;
          const parts = [];
          if (added > 0) parts.push(`${added} added`);
          if (updated > 0) parts.push(`${updated} updated`);
          if (dismissed > 0) parts.push(`${dismissed} dismissed as same`);
          notification.success({
            message: 'Enrollment completed',
            description: parts.length
              ? parts.join(' · ')
              : (body.message || `${data?.applied_count ?? data?.row_count ?? 0} row(s) applied.`),
          });
          loadOnboardingRecords(pagination.current, pagination.pageSize);
        }
      } else {
        const rowErrors = extractUploadRowErrors(response);
        const message = formatPlanEnrollmentErrorMessage(body || response, {
          fallback: 'Failed to start CSV upload',
        });
        if (rowErrors.length) {
          setUploadRowErrors({ message, rows: rowErrors });
        } else {
          notification.error({ message: 'CSV Upload Failed', description: message });
        }
        setUploading(false);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      const rowErrors = extractUploadRowErrors(error);
      const message = formatPlanEnrollmentErrorMessage(error, {
        fallback: error?.message || 'An unexpected error occurred',
      });
      if (rowErrors.length) {
        setUploadRowErrors({
          message: message || 'Some rows could not be imported',
          rows: rowErrors,
        });
      } else {
        notification.error({ message: 'CSV Upload Failed', description: message });
      }
      setUploading(false);
    }
  };

  // Handle upload with clean confirmation
  const initiateUpload = (file) => {
    // Clear any previous upload file and confirmation state
    setUploadFile(null);
    setShowCleanConfirm(false);
    
    if (cleanExisting) {
      setUploadFile(file);
      setShowCleanConfirm(true);
    } else {
      handleCSVUpload(file, false);
    }
  };

  // Confirm clean and upload
  const confirmCleanAndUpload = () => {
    setShowCleanConfirm(false);
    if (uploadFile) {
      const fileToUpload = uploadFile;
      // Clear uploadFile before starting upload to allow new file selection
      setUploadFile(null);
      handleCSVUpload(fileToUpload, true);
    }
  };

  // Download CSV template
  const downloadCSVTemplate = () => {
    const csvContent = `name,phone,email,category,social_no,insurance_package,premium_type,state,city,additional_heads
John Doe,08012345678,john@example.com,corporate,CORP001,Premium Family,family,Lagos,Ikeja,2
Jane Roe,08023456789,,corporate,CORP001,Premium Individual,individual,Abuja,Abuja,0
Mike Smith,08034567890,mike@example.com,self pay,,Basic Individual,individual,Port Harcourt,Port Harcourt,0`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'policies_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download CSV Upload Report
  const downloadUploadReport = async (jobId) => {
    if (!jobId) {
      notification.error({
        message: 'Download Failed',
        description: 'No job ID available'
      });
      return;
    }

    try {
      notification.info({
        message: 'Downloading Report',
        description: 'Your report is being downloaded...',
        duration: 2
      });

      // Use myApi to handle proper URL construction and authentication
      const response = await myApi('GET', `/v2/corporations/csv-jobs/${jobId}/report`, null, {
        responseType: 'blob'
      });

      // myApi returns response in response.data for blob
      const blob = response.data || response;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_upload_report_${jobId}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notification.success({
        message: 'Report Downloaded',
        description: 'Your upload report has been downloaded successfully'
      });
    } catch (error) {
      console.error('Error downloading report:', error);
      notification.error({
        message: 'Download Failed',
        description: error.message || 'Unable to download the report'
      });
    }
  };

  // Reset upload modal
  const resetUploadModal = () => {
    setUploadModalVisible(false);
    setUploadFile(null);
    setUploadResults(null);
    setUploadRowErrors(null);
    setUploadProgress(0);
    setUploading(false);
    // Don't reset cleanExisting - preserve user's choice
    // setCleanExisting(false);
    setShowCleanConfirm(false);
    setUploadStatus(null);
    setUploadJobId(null);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Contact',
      key: 'contact',
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div>{record.formatted_phone || '—'}</div>
          {record.email ? <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text> : null}
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 140,
      render: (_, record) => (
        <Text style={{ fontSize: 12 }}>
          {[record.city, record.state].filter(Boolean).join(', ') || '—'}
        </Text>
      ),
    },
    {
      title: 'Package',
      key: 'insurance_and_premium',
      width: 130,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>{(record.insurance_package || '—').toUpperCase()}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.premium_type || '—'}</Text>
        </div>
      ),
    },
    {
      title: 'Heads',
      dataIndex: 'additional_heads',
      key: 'additional_heads',
      width: 70,
      align: 'center',
      render: (heads) => heads || 0,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={status === 1 ? 'success' : 'default'} style={{ margin: 0 }}>
          {status === 1 ? 'Complete' : 'Incomplete'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_date',
      key: 'created_date',
      width: 110,
      render: (date) => (date ? moment(date).format('MMM D, YYYY') : '—'),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => {
                setSelectedLead(record);
                setViewModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setSelectedOnboarding(record);
                editForm.setFieldsValue(record);
                setSelectedPlanId(orgPlanOptions.find((o) => o.value === record.insurance_package || o.plan_name === record.insurance_package)?.plan_id ?? null);
                setEditModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            onConfirm={() => handleDeleteOnboarding(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  return (
    <>
      {progress ? <BulkUploadProgress progress={progress} /> : null}

      {enrollmentPause ? (
        <EnrollmentPausedBanner
          data={enrollmentPause}
          socialNo={orgSocialNo}
          style={{ marginBottom: 12 }}
          downloading={exportingConflicts}
          onDownload={async () => {
            setExportingConflicts(true);
            try {
              await lib.exportEnrollmentConflictsCsv({
                session_id: enrollmentPause.session_id,
                social_no: orgSocialNo,
                status: 'open',
              });
            } catch (err) {
              notification.error({ message: err?.message || 'Export failed' });
            } finally {
              setExportingConflicts(false);
            }
          }}
        />
      ) : null}

      <div className="org-detail-toolbar">
        <Search
          className="org-detail-toolbar__search"
          placeholder="Search by name, email, or phone…"
          allowClear
          size="small"
          onSearch={handleSearch}
        />
        <Space size={8} wrap>
          {isConnected ? <Tag color="success" style={{ margin: 0 }}>Live</Tag> : null}
          <Button size="small" icon={<FilterOutlined />} onClick={() => setFilterDrawerVisible(true)}>
            Filters
          </Button>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
            Add record
          </Button>
          <Button
            size="small"
            icon={<UploadOutlined />}
            onClick={() => {
              setUploadFile(null);
              setShowCleanConfirm(false);
              setUploadResults(null);
              setUploadStatus(null);
              setUploadJobId(null);
              setUploadProgress(0);
              setEnrollmentPause(null);
              setUploadModalVisible(true);
            }}
          >
            Upload CSV
          </Button>
          <Button size="small" icon={<ReloadOutlined spin={loading} />} onClick={handleRefresh} loading={loading}>
            Reload
          </Button>
        </Space>
      </div>

      {selectedRowKeys.length > 0 ? (
        <div className="org-onboarding-bulk-bar">
          <Text strong>{selectedRowKeys.length} selected</Text>
          <Space size={8} wrap>
            <Button
              size="small"
              type="primary"
              icon={<MailOutlined />}
              onClick={() => handleBulkAction('send_welcome_email')}
              loading={bulkActionLoading}
            >
              Send welcome emails
            </Button>
            <Button size="small" onClick={() => handleBulkAction('activate')} loading={bulkActionLoading}>
              Activate
            </Button>
            <Button size="small" onClick={() => handleBulkAction('deactivate')} loading={bulkActionLoading}>
              Deactivate
            </Button>
            <Button size="small" danger onClick={() => handleBulkAction('delete')} loading={bulkActionLoading}>
              Delete
            </Button>
          </Space>
        </div>
      ) : null}

      <Table
        className="org-policies-table"
        columns={columns}
        dataSource={onboardingRecords}
        loading={loading}
        size="small"
        rowKey={(record) => record.id}
        rowSelection={rowSelection}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            loadOnboardingRecords(page, pageSize);
          },
        }}
        scroll={{ x: 980 }}
        locale={{ emptyText: <Text type="secondary">No pending onboarding records found</Text> }}
      />


      {/* Filter Drawer */}
      <Drawer
        title="Filter Pending Onboarding Records"
        placement="right"
        width={400}
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">

          <div>
            <Text strong>Premium Type</Text>
            <Select
              style={{ width: '100%', marginTop: '8px' }}
              placeholder="Select premium type"
              allowClear
              value={filters.premium_type}
              onChange={(value) => handleFilterChange('premium_type', value)}
            >
              <Option value="individual">Individual</Option>
              <Option value="family">Family</Option>
            </Select>
          </div>

          <div>
            <Text strong>State</Text>
            <Input
              style={{ marginTop: '8px' }}
              placeholder="Enter state"
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
            />
          </div>

          <div>
            <Text strong>City</Text>
            <Input
              style={{ marginTop: '8px' }}
              placeholder="Enter city"
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            />
          </div>

          <div>
            <Text strong>Status</Text>
            <Select
              style={{ width: '100%', marginTop: '8px' }}
              placeholder="Select status"
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="1">Active</Option>
              <Option value="0">Inactive</Option>
            </Select>
          </div>

          <div>
            <Text strong>Additional Heads</Text>
            <Row gutter={8} style={{ marginTop: '8px' }}>
              <Col span={12}>
                <Input
                  placeholder="Min"
                  type="number"
                  value={filters.additional_heads_min}
                  onChange={(e) => handleFilterChange('additional_heads_min', e.target.value)}
                />
              </Col>
              <Col span={12}>
                <Input
                  placeholder="Max"
                  type="number"
                  value={filters.additional_heads_max}
                  onChange={(e) => handleFilterChange('additional_heads_max', e.target.value)}
                />
              </Col>
            </Row>
          </div>

          <div>
            <Text strong>Created Date Range</Text>
            <RangePicker
              style={{ width: '100%', marginTop: '8px' }}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange('created_date_from', dates[0]);
                  handleFilterChange('created_date_to', dates[1]);
                } else {
                  handleFilterChange('created_date_from', '');
                  handleFilterChange('created_date_to', '');
                }
              }}
            />
          </div>

          <Divider />

          <Space style={{ width: '100%', justifyContent: 'center' }}>
            <Button 
              onClick={clearFilters}
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: '#3497D3',
                color: '#3497D3'
              }}
            >
              Clear All Filters
            </Button>
            <Button 
              type="primary" 
              onClick={() => {
                setFilterDrawerVisible(false);
                loadOnboardingRecords(1, pagination.pageSize);
              }}
              style={{
                backgroundColor: '#3497D3',
                borderColor: '#3497D3'
              }}
            >
              Apply Filters
            </Button>
          </Space>
        </Space>
      </Drawer>

      {/* Create Policy Modal */}
      <Modal
        title="Add New Pre-Enrollment Record"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="How adding a client works now"
            description="Pick the insurance package, then the premium: Individual or Family (and the family size the organization offers), then any additional dependents by age category and quantity. The total is priced by the server and shown before you save; only sub-bands priced for this organization are offered."
          />
          <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateBulkUpload}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter name' }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  { required: true, message: 'Please enter phone number' },
                  { min: 10, message: 'Phone must be at least 10 digits' },
                  { max: 15, message: 'Phone must not exceed 15 digits' }
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={orgSocialNo ? 24 : 12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
            {orgSocialNo ? (
              <Form.Item name="category" hidden initialValue="corporate">
                <Input type="hidden" />
              </Form.Item>
            ) : (
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="Category"
                >
                  <Select placeholder="Select category">
                    <Option value="corporate">Corporate</Option>
                    <Option value="self pay">Self Pay</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col span={12}>
{renderPremiumOrBandField(createForm)}
            </Col>
            <Col span={12}>
              <Form.Item
                name="additional_heads"
                label="Additional Heads"
              >
                <Input type="number" min={0} placeholder="Number of additional heads" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="state"
                label={cfg.fieldProps('org_client', 'state', { label: 'State' }).label}
                rules={cfg.fieldProps('org_client', 'state', {}).required ? [{ required: true, message: 'State is required' }] : undefined}
                hidden={cfg.isHidden('org_client', 'state')}
              >
                <Select showSearch optionFilterProp="label" placeholder="Select state" options={cfg.options('state', stateOptions)} allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label={cfg.fieldProps('org_client', 'city', { label: 'City' }).label}
                rules={cfg.fieldProps('org_client', 'city', {}).required ? [{ required: true, message: 'City is required' }] : undefined}
                hidden={cfg.isHidden('org_client', 'city')}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="social_no"
                label="Organization code"
              >
                <Input placeholder="Enter organization code" disabled={!!orgSocialNo} />
              </Form.Item>
            </Col>
            <Col span={12}>
              {renderInsurancePackageField()}
            </Col>
          </Row>

          {/* <Form.Item
            name="status"
            label="Status"
            initialValue={1}
          >
            <Select>
              <Option value={1}>Active</Option>
              <Option value={0}>Inactive</Option>
            </Select>
          </Form.Item> */}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setCreateModalVisible(false)}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: '#3497D3',
                  color: '#3497D3'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                disabled={!orgPlanOptions.length}
                style={{
                  backgroundColor: '#3497D3',
                  borderColor: '#3497D3'
                }}
              >
                Create Record
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Policy Modal */}
      <Modal
        title="Edit Pre-Enrollment Record"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedOnboarding(null);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateOnboarding}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter name' }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  { required: true, message: 'Please enter phone number' },
                  { min: 10, message: 'Phone must be at least 10 digits' },
                  { max: 15, message: 'Phone must not exceed 15 digits' }
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={orgSocialNo ? 24 : 12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
            {orgSocialNo ? (
              <Form.Item name="category" hidden>
                <Input type="hidden" />
              </Form.Item>
            ) : (
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="Category"
                >
                  <Select placeholder="Select category">
                    <Option value="corporate">Corporate</Option>
                    <Option value="self pay">Self Pay</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col span={12}>
{renderPremiumOrBandField(editForm)}
            </Col>
            <Col span={12}>
              <Form.Item
                name="additional_heads"
                label="Additional Heads"
              >
                <Input type="number" min={0} placeholder="Number of additional heads" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="state"
                label={cfg.fieldProps('org_client', 'state', { label: 'State' }).label}
                rules={cfg.fieldProps('org_client', 'state', {}).required ? [{ required: true, message: 'State is required' }] : undefined}
                hidden={cfg.isHidden('org_client', 'state')}
              >
                <Select showSearch optionFilterProp="label" placeholder="Select state" options={cfg.options('state', stateOptions)} allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label={cfg.fieldProps('org_client', 'city', { label: 'City' }).label}
                rules={cfg.fieldProps('org_client', 'city', {}).required ? [{ required: true, message: 'City is required' }] : undefined}
                hidden={cfg.isHidden('org_client', 'city')}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="social_no"
                label="Organization code"
              >
                <Input placeholder="Enter organization code" />
              </Form.Item>
            </Col>
            <Col span={12}>
              {renderInsurancePackageField()}
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setEditModalVisible(false);
                  setSelectedOnboarding(null);
                  editForm.resetFields();
                }}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: '#3497D3',
                  color: '#3497D3'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                disabled={!orgPlanOptions.length}
                style={{
                  backgroundColor: '#3497D3',
                  borderColor: '#3497D3'
                }}
              >
                Update Record
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        title="Pre-Enrollment Record Details"
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedLead(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setViewModalVisible(false);
            setSelectedLead(null);
          }}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedLead && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={4} style={{ marginBottom: 16 }}>
                  {selectedLead.name}
                </Title>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Phone:</Text>
                  <br />
                  <Text>{selectedLead.formatted_phone || 'N/A'}</Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Email:</Text>
                  <br />
                  <Text>{selectedLead.email || 'N/A'}</Text>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Category:</Text>
                  <br />
                  <Tag color={selectedLead.category === 'corporate' ? 'blue' : 'green'}>
                    {selectedLead.category?.toUpperCase()}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Premium Type:</Text>
                  <br />
                  <Tag color={selectedLead.premium_type === 'family' ? 'purple' : 'cyan'}>
                    {selectedLead.premium_type?.toUpperCase()}
                  </Tag>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Location:</Text>
                  <br />
                  <Text>{selectedLead.city}, {selectedLead.state}</Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Additional Heads:</Text>
                  <br />
                  <Text>{selectedLead.additional_heads || 0}</Text>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Status:</Text>
                  <br />
                  <Badge 
                    status={selectedLead.status === 1 ? 'success' : 'default'} 
                    text={selectedLead.status === 1 ? 'Onboarded' : 'Not Onboarded'}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Created:</Text>
                  <br />
                  <Text>{moment(selectedLead.created_date).format('MMM DD, YYYY')}</Text>
                </div>
              </Col>
            </Row>

            {selectedLead.org_address && (
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Address:</Text>
                    <br />
                    <Text>{selectedLead.org_address}</Text>
                  </div>
                </Col>
              </Row>
            )}

            {selectedLead.social_no && (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Organization code:</Text>
                    <br />
                    <Text>{selectedLead.social_no}</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Insurance Package:</Text>
                    <br />
                    <Text>{selectedLead.insurance_package || 'N/A'}</Text>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        )}
      </Modal>

      {/* CSV Upload Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <UploadOutlined style={{ marginRight: '8px' }} />
            Upload Onboarding CSV
          </div>
        }
        open={uploadModalVisible}
        zIndex={1000}
        onCancel={resetUploadModal}
        footer={null}
        width={700}
        destroyOnClose
      >
        <div style={{ padding: '20px 0' }}>
          {enrollmentPause ? (
            <EnrollmentPausedBanner
              data={enrollmentPause}
              socialNo={orgSocialNo}
              style={{ marginBottom: 16 }}
              downloading={exportingConflicts}
              onDownload={async () => {
                setExportingConflicts(true);
                try {
                  await lib.exportEnrollmentConflictsCsv({
                    session_id: enrollmentPause.session_id,
                    social_no: orgSocialNo,
                    status: 'open',
                  });
                } catch (err) {
                  notification.error({ message: err?.message || 'Export failed' });
                } finally {
                  setExportingConflicts(false);
                }
              }}
            />
          ) : null}
          {!uploadResults ? (
            <div>
              {/* Per-row validation errors from the server */}
              {uploadRowErrors ? (
                <div style={{ marginBottom: '20px' }}>
                  <Alert
                    type="error"
                    showIcon
                    message={uploadRowErrors.message || 'Upload rejected'}
                    description={`${uploadRowErrors.rows.length} row${uploadRowErrors.rows.length === 1 ? '' : 's'} could not be imported. Fix the issues below and upload again — nothing was saved.`}
                    action={(
                      <Button size="small" onClick={() => setUploadRowErrors(null)}>
                        Dismiss
                      </Button>
                    )}
                    style={{ marginBottom: '12px' }}
                  />
                  <Table
                    size="small"
                    rowKey={(r) => r.row ?? Math.random()}
                    dataSource={uploadRowErrors.rows}
                    pagination={uploadRowErrors.rows.length > 8 ? { pageSize: 8, size: 'small' } : false}
                    scroll={{ y: 260 }}
                    columns={[
                      {
                        title: 'Row',
                        dataIndex: 'row',
                        width: 70,
                        render: (value) => <Text strong>{value ?? '—'}</Text>,
                      },
                      {
                        title: 'Issues',
                        key: 'errors',
                        render: (_, record) => {
                          const errs = Array.isArray(record.errors)
                            ? record.errors
                            : (record.errors ? [record.errors] : []);
                          if (!errs.length) {
                            const single = record.error || record.message || record.result_message;
                            if (single) {
                              const formatted = formatPlanEnrollmentRowError(single);
                              return (
                                <Space size={4} wrap>
                                  <Text type="danger">{formatted.text}</Text>
                                  {formatted.code ? <Tag color="error">{formatted.code}</Tag> : null}
                                </Space>
                              );
                            }
                            return <Text type="secondary">Unknown error</Text>;
                          }
                          return (
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {errs.map((err, idx) => {
                                const formatted = formatPlanEnrollmentRowError(err);
                                return (
                                  <li key={idx}>
                                    <Space size={4} wrap>
                                      <Text type="danger">{formatted.text}</Text>
                                      {formatted.code ? <Tag color="error">{formatted.code}</Tag> : null}
                                    </Space>
                                  </li>
                                );
                              })}
                            </ul>
                          );
                        },
                      },
                    ]}
                  />
                  <Divider />
                </div>
              ) : null}

              {/* Instructions */}
              <Alert
                message="CSV Upload Instructions"
                description={
                  <div>
                    <p>Upload a CSV file with policies data. The file must include the following headers:</p>
                    <ul style={{ marginTop: '8px', marginBottom: '16px' }}>
                      <li><strong>Required columns:</strong> name, phone, email, category, organization code, insurance package, premium type, state, city, additional heads</li>
                      <li><strong>Insurance package:</strong> must match a plan assigned to this organization via Manage plans
                        {orgPlanOptions.length > 0
                          ? ` (allowed: ${orgPlanOptions.map((p) => p.plan_name || p.value).join(', ')})`
                          : ' (none assigned yet — assign plans first)'}
                      </li>
                    </ul>
                    <p><strong>Category options:</strong> corporate, self pay</p>
                    <p><strong>Premium type options:</strong> individual, family</p>
                    <p style={{ marginTop: 8, fontSize: 12, color: '#595959' }}>
                      CSV header spelling: name, phone, email, category, social_no, insurance_package, premium_type, state, city, additional_heads
                    </p>
                  </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: '20px' }}
              />

              {/* Template Download */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={downloadCSVTemplate}
                  style={{ marginBottom: '10px' }}
                >
                  Download CSV Template
                </Button>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Download the template to see the correct format
                </Text>
              </div>

              {/* Clean Existing Option */}
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={cleanExisting}
                    onChange={(e) => setCleanExisting(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  <div>
                    <Text strong style={{ display: 'block' }}>Replace all existing policies</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ⚠️ Warning: This will delete all current pending onboarding records for this organization before uploading new data. This action cannot be undone.
                    </Text>
                  </div>
                </label>
              </div>

              {/* File Upload */}
              <div style={{ textAlign: 'center' }}>
                <Upload.Dragger
                  accept=".csv"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                      notification.error({
                        message: 'Invalid File Type',
                        description: 'Please upload a CSV file'
                      });
                      return false;
                    }
                    initiateUpload(file);
                    return false;
                  }}
                  disabled={uploading}
                >
                  <p className="ant-upload-drag-icon">
                    <FileTextOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                  </p>
                  <p className="ant-upload-text" style={{ fontSize: '16px', marginBottom: '8px' }}>
                    Click or drag CSV file here to upload
                  </p>
                  <p className="ant-upload-hint" style={{ fontSize: '14px' }}>
                    Support for CSV files only • {cleanExisting ? 'REPLACE MODE' : 'Incremental Mode'}
                  </p>
                </Upload.Dragger>

                {/* Progress Section for async job */}
                {uploadStatus && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text strong>
                        {uploadStatus?.status === 'queued' && 'Queued for processing...'}
                        {uploadStatus?.status === 'processing' && 'Processing...'}
                        {uploadStatus?.status === 'completed' && 'Upload Complete!'}
                        {uploadStatus?.status === 'failed' && 'Upload Failed'}
                      </Text>
                      <Text>{Number(uploadStatus?.progress_percentage) || 0}%</Text>
                    </div>
                    <Progress
                      percent={Number(uploadStatus?.progress_percentage) || 0}
                      status={uploadStatus?.status === 'failed' ? 'exception' : (uploadStatus?.status === 'completed' ? 'success' : 'active')}
                    />
                    {uploadStatus?.status === 'processing' && (
                      <Text style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                        Processing {uploadStatus?.processed_rows || 0} of {uploadStatus?.total_rows || 0} rows...
                      </Text>
                    )}
                    {(uploadStatus?.status === 'completed' || uploadStatus?.status === 'failed') && uploadJobId && (
                      <div style={{ textAlign: 'center', marginTop: 12 }}>
                        <Button 
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={() => downloadUploadReport(uploadJobId)}
                          size="small"
                        >
                          Download Detailed Report
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Upload Results */
            <div>
              <Result
                status="success"
                title={uploadStatus?.status === 'failed' ? 'CSV Upload Failed' : 'CSV Upload Completed'}
                subTitle={uploadStatus?.status === 'failed' ? (uploadStatus?.error_message || 'An error occurred during processing') : `Processed ${uploadResults.total_rows} rows successfully`}
              />

              {/* Summary Statistics */}
              <Row gutter={16} style={{ marginBottom: '20px' }}>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Total Rows"
                      value={uploadResults.total_rows}
                      prefix={<FileTextOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Valid Rows"
                      value={uploadResults.valid_rows}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Successful"
                      value={uploadStatus?.successful_rows ?? uploadResults.inserted}
                      prefix={<PlusOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Detailed Results */}
              {/* {(uploadResults.details && uploadResults.details.length > 0) && (
                <div>
                  <Title level={5}>Processing Details</Title>
                  <List
                    size="small"
                    dataSource={uploadResults.details}
                    renderItem={(item, index) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            item.status === 'failed' ? (
                              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                            ) : (
                              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                            )
                          }
                          title={`Row ${item.row}: ${item.status}`}
                          description={
                            item.errors ? (
                              <div>
                                <Text type="danger">Errors: {item.errors.join(', ')}</Text>
                              </div>
                            ) : (
                              <Text type="warning">{item.onBoardingStatusDetail || item.reason}</Text>
                            )
                          }
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )} */}

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Space>
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={() => downloadUploadReport(uploadJobId)}
                    disabled={!uploadJobId}
                  >
                    Download Report
                  </Button>
                  <Button onClick={resetUploadModal}>
                    Upload Another File
                  </Button>
                  <Button type="primary" onClick={resetUploadModal}>
                    Close
                  </Button>
                </Space>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Confirmation Modal for Clean/Replace Mode */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
            <span>Confirm Replace Mode</span>
          </div>
        }
        open={showCleanConfirm}
        zIndex={2000}
        onCancel={() => {
          setShowCleanConfirm(false);
          setUploadFile(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setShowCleanConfirm(false);
            setUploadFile(null);
          }}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={confirmCleanAndUpload}
          >
            Yes, Replace All Onboarding Records
          </Button>
        ]}
      >
        <div style={{ padding: '16px 0' }}>
          <Alert
            message="Warning: Destructive Action"
            description={
              <div>
                <p>You are about to <strong>delete all existing onboarding records</strong> for <strong>{organizationData?.org_name || 'this organization'}</strong> and replace them with the new data from your CSV file.</p>
                <p style={{ marginTop: 12, marginBottom: 0 }}>
                  <strong>This action cannot be undone.</strong> Please make sure you have a backup of your data before proceeding.
                </p>
              </div>
            }
            type="error"
            showIcon
          />
        </div>
      </Modal>
    </>
  );
};

export default BulkUploadManagement;
